import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Router } from '@angular/router';
import dayjs from 'dayjs';
import {
  BookingDTO,
  SimpleBookingDTO,
  SimpleHourBookingDTO,
} from '@lkp/parking-sdk';
import { Subject } from 'rxjs';
import { DateService } from 'src/app/core/services/date.service';

@Component({
    selector: 'app-bookings-table',
    templateUrl: './bookings-table.component.html',
    styleUrls: ['./bookings-table.component.scss'],
    standalone: false
})
export class BookingsTableComponent {
  @Input() bookings!: (BookingDTO | SimpleBookingDTO | SimpleHourBookingDTO)[];
  @Input() topic: 'arrival' | 'departure' | 'all' | 'parked' = 'all';
  @Input() totalPassengers?: number;
  @Input() coloredSearchResult: boolean = false;
  @Input() showServicesColumn?: Boolean;
  @Input() showTravelHomeFromColumn?: Boolean;
  @Input() showCommentsColumn?: Boolean;
  @Input() showParkTimeColumn?: Boolean;
  @Input() compactCommentsHeader: boolean = false;
  @Input() compactPassengersHeader: boolean = false;
  @Input() wideArrivalDateColumn: boolean = false;
  @Input() sortOrder: 'asc' | 'desc' | undefined = undefined;
  @Input() prevTargetColumn: string | undefined = undefined;
  @Output() closeDrawer: EventEmitter<any> = new EventEmitter();
  @Output() sortColumnClick: EventEmitter<any> = new EventEmitter();
  displayedColumns?: string[];
  readonly onDestroy$ = new Subject<void>();

  constructor(
    readonly router: Router,
    readonly dateService: DateService
  ) {}

  ngOnInit() {
    /* Set displayed columns */
    switch (this.topic) {
      case 'arrival':
        this.displayedColumns = this.arrivalColumns();
        break;
      case 'departure':
        this.displayedColumns = this.departureColumns();
        break;
      case 'parked':
        this.displayedColumns = [
          'bookingNumber',
          'registrationNumber',
          'flightNumber',
          'arrivalDate',
          'services',
          'comments',
        ];
        break;
      default:
        this.displayedColumns = [
          'bookingNumber',
          'registrationNumber',
          'location',
          'flightNumber',
          'departureDate',
          'arrivalDate',
          'qtyPersons',
          'comments',
        ];
    }
  }

  showBooking(row: any) {
    // open single page view, booking is fetched by the booking page
    this.router.navigate(['bokning', row.bookingId]);
  }

  formatDate(date: string | Date): string {
    return this.dateService.formatDate(date);
  }

  formatMonthAndDay(date: string | Date): string {
    return dayjs(date).format('DD/MM');
  }

  formatTime(date: string | Date): string {
    return this.dateService.formatTime(date);
  }
  close() {
    this.closeDrawer.emit();
  }

  arrivalColumns() {
    const defaults = [
      'bookingNumber',
      'registrationNumber',
      'qtyPersons',
      'childSafetySeat',
      'paymentStatus',
      'location',
    ];
    if (!this.showTravelHomeFromColumn) defaults.push('handLuggage');
    if (this.showTravelHomeFromColumn)
      defaults.splice(2, 1, 'flightNumber', 'travelHomeFrom'); // Replace childeseat if picked up
    if (this.showParkTimeColumn) defaults.splice(1, 0, 'parkTime');
    if (this.showServicesColumn) defaults.splice(5, 0, 'services'); // Put car wash column before handluggage column to match icons order
    if (this.showCommentsColumn) defaults.push('comments');
    defaults.push('drivenPassengers');

    return defaults;
  }

  departureColumns() {
    const defaults = [
      'bookingNumber',
      'registrationNumber',
      'departureDate',
      'qtyPersons',
      'flightNumber',
      'childSafetySeat',
      'paymentStatus',
      'location',
    ];

    if (this.showServicesColumn) defaults.push('services');
    if (this.showCommentsColumn) defaults.push('comments');
    defaults.push('drivenPassengers');
    return defaults;
  }

  preventRowClick(event: any) {
    event.stopPropagation();
  }

  sortColumn(event: Event) {
    const target = event.target as HTMLTableCellElement;
    const targetColumn = target.getAttribute('data-column');
    switch (this.sortOrder) {
      case undefined:
        this.sortColumnClick.emit({
          column: targetColumn ? targetColumn : undefined,
          sort: 'asc',
        });
        break;
      case 'asc':
        this.sortColumnClick.emit({
          column: targetColumn || undefined,
          sort: this.prevTargetColumn === targetColumn ? 'desc' : 'asc',
        });
        break;
      case 'desc':
        this.sortColumnClick.emit({
          column:
            this.prevTargetColumn === targetColumn || !targetColumn
              ? undefined
              : targetColumn,
          sort: this.prevTargetColumn === targetColumn ? undefined : 'asc',
        });
        break;
    }
  }

  getChildSafetyInfo(element: any) {
    if (element.childSafetySeat && element.boosterSeat) {
      return 'Bilbarnstol, Bälteskudde';
    } else if (element.boosterSeat) {
      return 'Bälteskudde';
    } else if (element.childSafetySeat) {
      return 'Bilbarnstol';
    } else {
      return '-';
    }
  }

  isMissing(date: any) {
    const now = dayjs();
    return dayjs(date).isBefore(now);
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
